import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import apiConfig from 'constants/apiConfig';
import { GetIncidentPositionResponse } from 'components/IncidentConfig/incidentConfig.types';
import { useOptionalCreateRosterDraft } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';

export const keys = {
  base: ['incidentPosition'] as const,
  byIncident: (incidentId: string) => [...keys.base, incidentId] as const,
  byPosition: (incidentId: string, positionId: string) =>
    [...keys.byIncident(incidentId), positionId] as const,
};

export const useGetIncidentPositionInfo = (
  incidentId: string,
  positionId: string,
  onSuccess?: (data: GetIncidentPositionResponse) => void
) => {
  const draft = useOptionalCreateRosterDraft();
  const draftPosition = draft?.positions.find(
    (position) => position.id === positionId
  );
  const draftData = useMemo<GetIncidentPositionResponse | undefined>(() => {
    if (!draftPosition) return undefined;
    return {
      id: draftPosition.id,
      itemId: draftPosition.item_id,
      incidentId: '',
      name: draftPosition.name,
      parentId: draftPosition.parent_id,
      parentName: draftPosition.parent_name,
      isWorkgroup: draftPosition.is_workgroup,
      isTemplate: draftPosition.is_template,
      retireNextOp: draftPosition.retire_next_op,
      category: draftPosition.category,
      customCategory: draftPosition.custom_category,
      permissions: [],
    };
  }, [draftPosition]);

  useEffect(() => {
    if (draftData && onSuccess) onSuccess(draftData);
  }, [draftData, onSuccess]);

  const query = useQuery({
    queryKey: keys.byPosition(incidentId, positionId),
    queryFn: async () => {
      const response = await axios.get<GetIncidentPositionResponse>(
        `${apiConfig.apiGateway.incidentPositions}/${incidentId}/${positionId}`
      );

      if (onSuccess) {
        onSuccess(response.data);
      }

      return response.data;
    },
    staleTime: 0,
    enabled: !draft && !!incidentId && !!positionId,
  });

  if (draft) {
    return {
      ...query,
      data: draftData,
      isLoading: false,
      isPending: false,
      isFetching: false,
      isSuccess: !!draftData,
      isError: !draftData,
      isLoadingError: false,
      isRefetchError: false,
      error: draftData ? null : new Error('Position not found'),
      status: draftData ? ('success' as const) : ('error' as const),
    } as typeof query;
  }

  return query;
};
